import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LinksConfigCropModal from '../../components/LinksConfigCropModal';
import LinksConfigIdentityPanel from '../../components/LinksConfigIdentityPanel';
import LinksConfigAppearancePanel from '../../components/LinksConfigAppearancePanel';
import LinksConfigProductsPanel from '../../components/LinksConfigProductsPanel';
import LinksConfigPromotionPanel from '../../components/LinksConfigPromotionPanel';
import LinksConfigExternalLinksPanel from '../../components/LinksConfigExternalLinksPanel';
import LinksConfigButtonOrderPanel from '../../components/LinksConfigButtonOrderPanel';
import LinksConfigHero from '../../components/LinksConfigHero';
import LinksConfigPreview from '../../components/LinksConfigPreview';
import {
  PUBLIC_BASE,
  DEFAULT_THEME,
  DEFAULT_PRODUCT_STATE,
  makeExternalId,
} from '../../constants';
import { buildCustomColorsFromTheme, resolveTheme } from '../../theme';
import {
  buildButtonOrderItems,
  normalizeButtonOrder,
} from '../../utils/buttonOrder';
import {
  getLinkPageConfig,
  saveLinkPageConfig,
  uploadExternalLinkImage,
  uploadLinkPageImage,
} from '../../services/api';
import '../../styles/foodstation-links.css';
import '../../styles/links-config.css';
import {
  FALLBACK_RESTAURANT,
  normalizeUrl,
  apiErrorMessage,
  PROFILE_CROP_ZOOM_MAX,
  CROP_IMAGE_QUALITY,
  EMPTY_PROFILE_CROP,
  friendlyUploadErrorMessage,
  validateImageBeforeUpload,
  validateImageTypeBeforeUpload,
  cropDisplaySize,
  cropZoomMin,
  cropExportGeometry,
  clampCropPosition,
  reorderItems,
  normalizeConfig,
  configPayload,
} from './linksConfig.utils';

const LinksConfig = () => {
  const navigate = useNavigate();
  const cropImageRef = useRef(null);
  const cropDragRef = useRef(null);

  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [uploadingImage, setUploadingImage] = useState('');
  const [uploadError, setUploadError] = useState({ target: '', message: '' });
  const [profileCrop, setProfileCrop] = useState(EMPTY_PROFILE_CROP);
  const [croppingProfile, setCroppingProfile] = useState(false);

  const [restaurant, setRestaurant] = useState(FALLBACK_RESTAURANT);
  const [hoursCopyOptions, setHoursCopyOptions] = useState([]);
  const [products, setProducts] = useState(DEFAULT_PRODUCT_STATE);
  const [externalLinks, setExternalLinks] = useState([]);
  const [buttonOrder, setButtonOrder] = useState([]);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [active, setActive] = useState(true);
  const [publicMeta, setPublicMeta] = useState({ publicUrl: '', publicPath: '' });
  const [savedConfig, setSavedConfig] = useState(null);

  const [copied, setCopied] = useState(false);
  const [newLink, setNewLink] = useState({ label: '', url: '' });

  const applyConfig = useCallback((rawConfig) => {
    const config = normalizeConfig(rawConfig);
    setHoursCopyOptions(config.hoursCopyOptions);
    setRestaurant(config.restaurant);
    setProducts(config.products);
    setExternalLinks(config.externalLinks);
    setButtonOrder(config.buttonOrder);
    setTheme(config.theme);
    setActive(config.active);
    setPublicMeta({
      publicUrl: config.publicUrl,
      publicPath: config.publicPath,
    });
    setSavedConfig(configPayload(config));
  }, []);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setLoadError('');

    try {
      const config = await getLinkPageConfig();
      applyConfig(config);
      setLoaded(true);
    } catch (error) {
      setLoadError(apiErrorMessage(error, 'Não foi possível carregar o LinkPage.'));
    } finally {
      setLoading(false);
    }
  }, [applyConfig]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  useEffect(() => () => {
    if (profileCrop.previewUrl) {
      URL.revokeObjectURL(profileCrop.previewUrl);
    }
  }, [profileCrop.previewUrl]);

  const persistCurrentConfig = async () => {
    setSaving(true);
    setSaveError('');
    setSaveMessage('');

    try {
      const config = await saveLinkPageConfig({
        active,
        restaurant,
        products,
        externalLinks,
        buttonOrder,
        theme,
      });
      applyConfig(config);
      setSaveMessage('Alterações salvas.');
      return config;
    } catch (error) {
      setSaveError(apiErrorMessage(error, 'Não foi possível salvar as alterações.'));
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      await persistCurrentConfig();
    } catch {
      // A mensagem de erro é exibida no cabeçalho.
    }
  };

  const currentConfig = useMemo(() => ({
    active,
    restaurant,
    products,
    externalLinks,
    buttonOrder,
    theme,
  }), [active, buttonOrder, externalLinks, products, restaurant, theme]);

  const hasUnsavedChanges = Boolean(
    loaded
    && savedConfig
    && JSON.stringify(currentConfig) !== JSON.stringify(savedConfig)
  );

  useEffect(() => {
    if (hasUnsavedChanges) setSaveMessage('');
  }, [hasUnsavedChanges]);

  const applyUploadedConfig = useCallback((rawConfig, updateDraft) => {
    const config = normalizeConfig(rawConfig);
    setSavedConfig(configPayload(config));
    setPublicMeta({
      publicUrl: config.publicUrl,
      publicPath: config.publicPath,
    });
    updateDraft(config);
  }, []);

  const slug = restaurant.slug || '';
  const publicUrl = publicMeta.publicUrl
    ? publicMeta.publicUrl.replace(/^https?:\/\//i, '')
    : slug
      ? `${PUBLIC_BASE}/${slug}`
      : PUBLIC_BASE;
  const publicPath = publicMeta.publicPath || (slug ? `/links/${slug}` : '/links');
  const activeProductCount = products.filter((p) => p.enabled && p.available !== false).length;
  const isUploadingImage = Boolean(uploadingImage);
  const identityUploadError =
    uploadError.message && ['cover', 'profile'].includes(uploadError.target)
      ? uploadError.message
      : '';
  const externalUploadError =
    uploadError.message && uploadError.target.startsWith('external:')
      ? uploadError.message
      : '';
  const promotionUploadError =
    uploadError.message && uploadError.target === 'promotion'
      ? uploadError.message
      : '';
  const allButtonOrderItems = buildButtonOrderItems({
    products,
    externalLinks,
    restaurant,
    includeInactive: true,
  });
  const fullButtonOrder = normalizeButtonOrder(buttonOrder, allButtonOrderItems);
  const buttonOrderItems = buildButtonOrderItems({ products, externalLinks, restaurant });
  const effectiveButtonOrder = normalizeButtonOrder(buttonOrder, buttonOrderItems);
  const buttonOrderItemsByKey = new Map(buttonOrderItems.map((item) => [item.key, item]));
  const orderedButtonItems = effectiveButtonOrder
    .map((key) => buttonOrderItemsByKey.get(key))
    .filter(Boolean);
  const customColors = buildCustomColorsFromTheme(theme);
  const customColorsEnabled = customColors.enabled;
  const profileCropDisplay = cropDisplaySize(profileCrop);
  const profileCropImageStyle = profileCropDisplay.width
    ? {
      width: `${profileCropDisplay.width}px`,
      height: `${profileCropDisplay.height}px`,
      transform: `translate(-50%, -50%) translate(${profileCrop.x}px, ${profileCrop.y}px)`,
    }
    : {};
  const cropTarget = profileCrop.target || 'profile';
  const cropError = uploadError.target === cropTarget ? uploadError.message : '';

  const applyPreset = (preset) =>
    setTheme((current) => {
      const nextTheme = {
        ...current,
        presetId: preset.id,
        accent: preset.defaultAccent,
        buttonStyle: preset.defaultButtonStyle,
      };

      return {
        ...nextTheme,
        customColors: {
          ...buildCustomColorsFromTheme(nextTheme),
          enabled: false,
        },
      };
    });

  const updateAccent = (value) =>
    setTheme((current) => {
      const currentCustomColors = buildCustomColorsFromTheme(current);
      return {
        ...current,
        accent: value,
        customColors: currentCustomColors.enabled
          ? {
            ...currentCustomColors,
            accent: value,
          }
          : currentCustomColors,
      };
    });

  const toggleCustomColors = () =>
    setTheme((current) => {
      const currentCustomColors = buildCustomColorsFromTheme(current);
      return {
        ...current,
        customColors: {
          ...currentCustomColors,
          enabled: !currentCustomColors.enabled,
        },
      };
    });

  const updateCustomColor = (key, value) =>
    setTheme((current) => {
      const currentCustomColors = buildCustomColorsFromTheme(current);
      return {
        ...current,
        accent: key === 'accent' ? value : current.accent,
        customColors: {
          ...currentCustomColors,
          enabled: true,
          [key]: value,
        },
      };
    });

  const themeCover = resolveTheme(theme).cover;
  const coverEditStyle = restaurant.coverImage
    ? { backgroundImage: `url(${restaurant.coverImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: themeCover };

  const handleCopy = () => {
    const full = publicMeta.publicUrl || `https://${publicUrl}`;
    try {
      navigator.clipboard?.writeText(full);
    } catch {
      // Clipboard pode não estar disponível em alguns navegadores.
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const handleImageUpload = async (slot, file) => {
    if (!file) return;

    const validationError = validateImageBeforeUpload(file);
    if (validationError) {
      setUploadError({ target: slot, message: validationError });
      setSaveError(validationError);
      return;
    }

    setUploadingImage(slot);
    setSaveError('');
    setSaveMessage('');
    setUploadError({ target: '', message: '' });

    try {
      const config = await uploadLinkPageImage(slot, file);
      applyUploadedConfig(config, (saved) => {
        setRestaurant((current) => {
          if (slot === 'promotion') {
            return {
              ...current,
              promotion: saved.restaurant.promotion || { enabled: true, image: null },
            };
          }

          const imageKey = slot === 'cover' ? 'coverImage' : 'profileImage';
          return {
            ...current,
            [imageKey]: saved.restaurant[imageKey],
          };
        });
      });
    } catch (error) {
      const message = friendlyUploadErrorMessage(error, 'Não foi possível enviar a imagem.');
      setUploadError({ target: slot, message });
      setSaveError(message);
    } finally {
      setUploadingImage('');
    }
  };

  const handleProfileImageSelect = (file) => {
    openImageCrop(file, { target: 'profile' });
  };

  const handleCoverImageSelect = (file) => {
    openImageCrop(file, { target: 'cover' });
  };

  const handleExternalImageSelect = (linkId, file) => {
    if (!linkId) return;
    openImageCrop(file, { target: `external:${linkId}`, linkId });
  };

  const handlePromotionImageSelect = (file) => {
    openImageCrop(file, { target: 'promotion' });
  };

  const openImageCrop = (file, options = {}) => {
    if (!file) return;

    const validationError = validateImageTypeBeforeUpload(file);
    const target = options.target || 'profile';
    if (validationError) {
      setUploadError({ target, message: validationError });
      setSaveError(validationError);
      return;
    }

    setSaveError('');
    setUploadError({ target: '', message: '' });
    setProfileCrop({
      ...EMPTY_PROFILE_CROP,
      file,
      target,
      linkId: options.linkId || null,
      previewUrl: URL.createObjectURL(file),
    });
  };

  const closeProfileCrop = () => {
    cropDragRef.current = null;
    setCroppingProfile(false);
    setProfileCrop(EMPTY_PROFILE_CROP);
  };

  const handleProfileCropImageLoad = (event) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    setProfileCrop((current) => ({
      ...current,
      naturalWidth,
      naturalHeight,
      zoom: 1,
      x: 0,
      y: 0,
    }));
  };

  const handleProfileCropError = () => {
    const message = 'Não foi possível abrir essa imagem. Escolha outro arquivo.';
    setUploadError({ target: profileCrop.target || 'profile', message });
    setSaveError(message);
    closeProfileCrop();
  };

  const handleProfileCropPointerDown = (event) => {
    if (!profileCrop.previewUrl || !profileCrop.naturalWidth) return;

    event.preventDefault();
    cropDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      x: profileCrop.x,
      y: profileCrop.y,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleProfileCropPointerMove = (event) => {
    const drag = cropDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const nextX = drag.x + event.clientX - drag.startX;
    const nextY = drag.y + event.clientY - drag.startY;

    setProfileCrop((current) => {
      const nextPosition = clampCropPosition(current, nextX, nextY);
      return { ...current, ...nextPosition };
    });
  };

  const handleProfileCropPointerUp = (event) => {
    const drag = cropDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    cropDragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const handleProfileCropZoomChange = (event) => {
    const zoom = Number(event.target.value);
    setProfileCrop((current) => {
      const nextCrop = {
        ...current,
        zoom: Math.min(PROFILE_CROP_ZOOM_MAX, Math.max(cropZoomMin(current), zoom)),
      };
      return { ...nextCrop, ...clampCropPosition(nextCrop, nextCrop.x, nextCrop.y) };
    });
  };

  const createProfileCropFile = () =>
    new Promise((resolve, reject) => {
      const image = cropImageRef.current;
      const geometry = cropExportGeometry(profileCrop);
      if (!image || !geometry) {
        reject(new Error('Imagem não carregada.'));
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = geometry.width;
      canvas.height = geometry.height;
      const context = canvas.getContext('2d');
      if (!context) {
        reject(new Error('Não foi possível preparar o recorte.'));
        return;
      }

      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(
        image,
        geometry.imageX,
        geometry.imageY,
        geometry.imageWidth,
        geometry.imageHeight
      );

      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Não foi possível gerar a imagem recortada.'));
          return;
        }

        const fileName = profileCrop.target === 'cover'
          ? 'linkpage-capa'
          : profileCrop.target === 'promotion'
            ? 'linkpage-promocao'
          : profileCrop.target === 'profile'
            ? 'linkpage-foto-principal'
            : 'linkpage-link-externo';
        const extension = blob.type === 'image/webp' ? 'webp' : 'png';
        resolve(new File([blob], `${fileName}.${extension}`, { type: blob.type }));
      }, 'image/webp', CROP_IMAGE_QUALITY);
    });

  const handleApplyProfileCrop = async () => {
    setCroppingProfile(true);
    setSaveError('');
    setUploadError({ target: '', message: '' });

    try {
      const croppedFile = await createProfileCropFile();
      const validationError = validateImageBeforeUpload(croppedFile);
      const target = profileCrop.target || 'profile';
      if (validationError) {
        setUploadError({ target, message: validationError });
        setSaveError(validationError);
        return;
      }

      const linkId = profileCrop.linkId;
      closeProfileCrop();
      if (target.startsWith('external:') && linkId) {
        await handleExternalImageUpload(linkId, croppedFile);
      } else {
        await handleImageUpload(target, croppedFile);
      }
    } catch (error) {
      const message = error?.message || 'Não foi possível recortar essa imagem.';
      setUploadError({ target: profileCrop.target || 'profile', message });
      setSaveError(message);
    } finally {
      setCroppingProfile(false);
    }
  };

  const handleExternalImageUpload = async (linkId, file) => {
    if (!file) return;

    const target = `external:${linkId}`;
    const validationError = validateImageBeforeUpload(file);
    if (validationError) {
      setUploadError({ target, message: validationError });
      setSaveError(validationError);
      return;
    }

    setUploadingImage(target);
    setSaveError('');
    setSaveMessage('');
    setUploadError({ target: '', message: '' });

    const savedLinkExists = savedConfig?.externalLinks?.some((link) => link.id === linkId);
    if (!savedLinkExists) {
      const message = 'Salve o novo link antes de adicionar uma imagem.';
      setUploadError({ target, message });
      setSaveError(message);
      setUploadingImage('');
      return;
    }

    try {
      const config = await uploadExternalLinkImage(linkId, file);
      const savedLink = config.externalLinks?.find((link) => link.id === linkId);
      applyUploadedConfig(config, () => {
        setExternalLinks((current) => current.map((link) => (
          link.id === linkId ? { ...link, image: savedLink?.image || null } : link
        )));
      });
    } catch (error) {
      const message = friendlyUploadErrorMessage(error, 'Não foi possível enviar a imagem do link.');
      setUploadError({ target, message });
      setSaveError(message);
    } finally {
      setUploadingImage('');
    }
  };

  const removeCoverImage = () =>
    setRestaurant((current) => ({ ...current, coverImage: null }));

  const removeProfileImage = () =>
    setRestaurant((current) => ({ ...current, profileImage: null }));

  const removePromotionImage = () =>
    setRestaurant((current) => ({
      ...current,
      promotion: { enabled: false, image: null },
    }));

  const handleButtonOrderDragEnd = ({ source, destination }) => {
    if (!destination || destination.index === source.index) return;
    const visibleOrder = reorderItems(effectiveButtonOrder, source.index, destination.index);
    const visibleKeys = new Set(effectiveButtonOrder);
    const visibleQueue = [...visibleOrder];
    setButtonOrder(fullButtonOrder.map((key) =>
      visibleKeys.has(key) ? visibleQueue.shift() : key
    ));
  };

  const toggleProduct = (id) =>
    setProducts((prev) =>
      prev.map((p) => {
        if (p.id !== id || p.available === false) return p;
        return { ...p, enabled: !p.enabled };
      }),
    );

  const updateProductLabel = (id, label) =>
    setProducts((prev) =>
      prev.map((p) => (p.id === id && p.available !== false ? { ...p, label } : p)),
    );

  const addExternalLink = () => {
    const label = newLink.label.trim();
    const url = normalizeUrl(newLink.url);
    if (!label || !url) return;
    setExternalLinks((prev) => [...prev, { id: makeExternalId(), label, url }]);
    setNewLink({ label: '', url: '' });
  };

  const updateExternalLink = (id, patch) =>
    setExternalLinks((prev) =>
      prev.map((link) => (link.id === id ? { ...link, ...patch } : link)),
    );

  const removeExternalLink = (id) =>
    setExternalLinks((prev) => prev.filter((link) => link.id !== id));

  const removeExternalLinkImage = (id) =>
    updateExternalLink(id, { image: null });

  const toggleRestaurantFlag = (key) =>
    setRestaurant((current) => ({ ...current, [key]: current[key] === false ? true : !current[key] }));

  const toggleShowIcons = () =>
    setTheme((current) => ({ ...current, showIcons: current.showIcons === false }));

  const canAdd = newLink.label.trim() && newLink.url.trim();

  if (loading) {
    return (
      <div className="fsl-admin">
        <div className="fsl-state-card">Carregando LinkPage...</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="fsl-admin">
        <div className="fsl-state-card">
          <strong>{loadError}</strong>
          <button type="button" className="fsl-open-btn" onClick={loadConfig}>
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fsl-admin">
      <LinksConfigHero
        navigate={navigate}
        active={active}
        setActive={setActive}
        slug={slug}
        publicPath={publicPath}
        handleSave={handleSave}
        handleCopy={handleCopy}
        hasUnsavedChanges={hasUnsavedChanges}
        saving={saving}
        isUploadingImage={isUploadingImage}
        copied={copied}
        saveError={saveError}
        saveMessage={saveMessage}
      />

      <div className="fsl-admin-grid">
        <div className="fsl-config-col">
          <LinksConfigIdentityPanel
            hoursCopyOptions={hoursCopyOptions}
            restaurant={restaurant}
            setRestaurant={setRestaurant}
            coverEditStyle={coverEditStyle}
            identityUploadError={identityUploadError}
            isUploadingImage={isUploadingImage}
            uploadingImage={uploadingImage}
            removeCoverImage={removeCoverImage}
            removeProfileImage={removeProfileImage}
            handleCoverImageSelect={handleCoverImageSelect}
            handleProfileImageSelect={handleProfileImageSelect}
            toggleRestaurantFlag={toggleRestaurantFlag}
          />

          <LinksConfigPromotionPanel
            promotion={restaurant.promotion || { enabled: false, image: null }}
            setRestaurant={setRestaurant}
            uploadError={promotionUploadError}
            uploadingImage={uploadingImage}
            isUploadingImage={isUploadingImage}
            onSelectImage={handlePromotionImageSelect}
            onRemoveImage={removePromotionImage}
          />

          <LinksConfigAppearancePanel
            theme={theme}
            setTheme={setTheme}
            customColors={customColors}
            customColorsEnabled={customColorsEnabled}
            applyPreset={applyPreset}
            updateAccent={updateAccent}
            updateCustomColor={updateCustomColor}
            toggleCustomColors={toggleCustomColors}
            toggleShowIcons={toggleShowIcons}
          />

          <LinksConfigProductsPanel
            products={products}
            activeProductCount={activeProductCount}
            toggleProduct={toggleProduct}
            updateProductLabel={updateProductLabel}
          />

          <LinksConfigExternalLinksPanel
            externalLinks={externalLinks}
            externalUploadError={externalUploadError}
            uploadingImage={uploadingImage}
            isUploadingImage={isUploadingImage}
            handleExternalImageSelect={handleExternalImageSelect}
            removeExternalLinkImage={removeExternalLinkImage}
            updateExternalLink={updateExternalLink}
            removeExternalLink={removeExternalLink}
            newLink={newLink}
            setNewLink={setNewLink}
            canAdd={canAdd}
            addExternalLink={addExternalLink}
          />

          <LinksConfigButtonOrderPanel
            orderedButtonItems={orderedButtonItems}
            handleButtonOrderDragEnd={handleButtonOrderDragEnd}
          />
        </div>

        <LinksConfigPreview
          restaurant={restaurant}
          products={products}
          externalLinks={externalLinks}
          buttonOrder={effectiveButtonOrder}
          theme={theme}
          active={active}
        />
      </div>

      <LinksConfigCropModal
        profileCrop={profileCrop}
        croppingProfile={croppingProfile}
        cropError={cropError}
        profileCropImageStyle={profileCropImageStyle}
        cropImageRef={cropImageRef}
        onClose={closeProfileCrop}
        onApply={handleApplyProfileCrop}
        onImageLoad={handleProfileCropImageLoad}
        onImageError={handleProfileCropError}
        onPointerDown={handleProfileCropPointerDown}
        onPointerMove={handleProfileCropPointerMove}
        onPointerUp={handleProfileCropPointerUp}
        onZoomChange={handleProfileCropZoomChange}
      />
    </div>
  );
};

export default LinksConfig;
